import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import type { Theme } from '@mui/system';
import areaPrewiewImg from 'assets/indicators_preview/area.svg?url';
import barPreviewImg from 'assets/indicators_preview/bar.svg?url';
import donutPreviewImg from 'assets/indicators_preview/donut.svg?url';
import gaugePreviewImg from 'assets/indicators_preview/gauge.svg?url';
import horizontalBarPreviewImg from 'assets/indicators_preview/horizontal_bar.svg?url';
import linePreviewImg from 'assets/indicators_preview/line.svg?url';
import mapPreviewImg from 'assets/indicators_preview/map.svg?url';
import numberPreviewImg from 'assets/indicators_preview/number.svg?url';
import paragraphPreviewImg from 'assets/indicators_preview/paragraph.svg?url';
import piePreviewImg from 'assets/indicators_preview/pie.svg?url';
import pivotTablePreviewImg from 'assets/indicators_preview/pivot_table.svg?url';
import polarPreviewImg from 'assets/indicators_preview/polar.svg?url';
import scatterPlotPreviewImg from 'assets/indicators_preview/scatter.svg?url';
import tablePreviewImg from 'assets/indicators_preview/table.svg?url';
import treemapPreviewImg from 'assets/indicators_preview/treemap.svg?url';
import type { VizType } from 'business/definitions';
import { capitalize } from 'helpers/helpers';
import { translateBlock } from 'i18n';
import { INDICATOR_TAG_TYPES } from 'modules/decision/constants';
import { getAvailableFunctionsFromMetrics } from 'modules/decision/helpers/indicator';
import { getFilterLeaves } from 'modules/decision/pages/Explore/helpers/filterTree';
import type {
    IndicatorQueryT,
    IndicatorT,
    IndicatorTagT,
    IndicatorTagTypeT,
} from 'modules/decision/types/indicators';

export function concatenateSelectedValues(selectedValues?: string[]) {
    return selectedValues
        ?.map((domain) => capitalize(translateBlock(domain)))
        .join(', ');
}

const MAX_NUMBER_OF_CHARS_IN_CHIP = 25;

const VIZ_TYPE_PREVIEW_MAPPING: Record<VizType, string> = {
    advanced: paragraphPreviewImg,
    area: areaPrewiewImg,
    bar: barPreviewImg,
    donut: donutPreviewImg,
    gauge: gaugePreviewImg,
    horizontal_bar: horizontalBarPreviewImg,
    line: linePreviewImg,
    map: mapPreviewImg,
    number: numberPreviewImg,
    paragraph: paragraphPreviewImg,
    pie: piePreviewImg,
    pivot_table: pivotTablePreviewImg,
    polar_bar: polarPreviewImg,
    pyramid: horizontalBarPreviewImg, // Using horizontal bar as a placeholder for pyramid
    scatterplot: scatterPlotPreviewImg,
    table: tablePreviewImg,
    treemap: treemapPreviewImg,
};

export const getPreviewImageForVizType = (vizType: VizType): string =>
    VIZ_TYPE_PREVIEW_MAPPING[vizType];

export const getChipLabel = (
    value: string,
    aggregation?: string,
): { isTruncated: boolean; chipLabel: string } => {
    // Simple truncation and ellipsis addition if no aggregation is provided
    if (!aggregation) {
        if (value.length < MAX_NUMBER_OF_CHARS_IN_CHIP)
            return { isTruncated: false, chipLabel: value };
        return {
            isTruncated: true,
            chipLabel: `${value.slice(0, MAX_NUMBER_OF_CHARS_IN_CHIP)}...`,
        };
    }
    // With an aggregation, the ellipsis should be added before the parenthesis surrounding the aggregation
    const aggregationWithParenthesis = `(${aggregation})`;
    const fullValueLength =
        value.length + aggregationWithParenthesis.length + 1;
    if (fullValueLength < MAX_NUMBER_OF_CHARS_IN_CHIP)
        return {
            isTruncated: false,
            chipLabel: `${value} ${aggregationWithParenthesis}`,
        };
    return {
        isTruncated: true,
        chipLabel: `${value.slice(
            0,
            MAX_NUMBER_OF_CHARS_IN_CHIP - aggregationWithParenthesis.length,
        )}...${aggregationWithParenthesis}`,
    };
};

export function isCityIndicator(
    indicator: IndicatorQueryT,
): indicator is IndicatorT {
    return 'dashboardSlug' in indicator && indicator.dashboardSlug != null;
}

export function getChipColorFromIndicatorTag(
    indicatorFilterType: IndicatorTagTypeT,
    theme: Theme,
): string {
    const colorToTagMapper = {
        [INDICATOR_TAG_TYPES.connector]: theme.palette.green,
        [INDICATOR_TAG_TYPES.metric]: theme.palette.darkBlue,
        [INDICATOR_TAG_TYPES.dimension]: theme.palette.darkBlue,
        [INDICATOR_TAG_TYPES.filter]: theme.palette.darkBlue,
        [INDICATOR_TAG_TYPES.title]: theme.palette.darkGrey,
    };
    return colorToTagMapper[indicatorFilterType] as string;
}

export function getChipIconFromIndicatorTag(
    indicatorFilterType: IndicatorTagTypeT,
    color: string,
): JSX.Element | undefined {
    const iconByIndicatorTag: Partial<Record<IndicatorTagTypeT, JSX.Element>> =
        {
            [INDICATOR_TAG_TYPES.dimension]: (
                <ViewListOutlinedIcon style={{ color }} />
            ),
            [INDICATOR_TAG_TYPES.filter]: (
                <FilterAltOutlinedIcon style={{ color }} />
            ),
            [INDICATOR_TAG_TYPES.title]: (
                <AssessmentOutlinedIcon style={{ color }} />
            ),
        };
    return iconByIndicatorTag[indicatorFilterType];
}

export function getExploreBannerMessage(
    t: TranslationT,
    isEmpty: boolean,
    isNoResults: boolean,
): { title: string; description: string } | null {
    if (isEmpty)
        return {
            title: t('explore.carouselEmptyQueryTitle'),
            description: t('explore.carouselEmptyQuery'),
        };
    if (isNoResults)
        return {
            title: t('explore.carouselNoResultsTitle'),
            description: t('explore.carouselNoResults'),
        };
    return null;
}

type BannerMetadata = {
    bannerTitle: string;
    bannerSubtitle?: string;
    bannerId: string;
};

export function getBannerMeta(
    t: TranslationT,
    useExploreCard: boolean,
    isModel: boolean,
    count?: number,
): BannerMetadata {
    const countSuffix = count === undefined ? '' : ` (${count})`;
    if (useExploreCard) {
        return {
            bannerTitle: `${t('indicators.suggestedCharts')}${countSuffix}`,
            bannerId: 'indicators-banner-suggested',
        };
    }
    const type = isModel ? 'model' : 'city';
    const [titleKey, subtitleKey] = isModel
        ? ['indicators.modelIndicators', 'indicators.modelIndicatorsSubtitle']
        : ['indicators.cityIndicators', 'indicators.cityIndicatorsSubtitle'];
    return {
        bannerTitle: `${t(titleKey)}${countSuffix}`,
        bannerSubtitle: t(subtitleKey),
        bannerId: `indicators-banner-${type}`,
    };
}

export function isNewIndicator(createdAt: Date): boolean {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return new Date(createdAt) > thirtyDaysAgo;
}

export function getIndicatorTags(
    indicator: IndicatorQueryT,
    t: TranslationT,
): IndicatorTagT[] {
    const indicatorMetricTags = indicator.metrics.map((metric) => ({
        aggregation: t(`sentence:metricAggregations.${metric.aggregation}`),
        value: capitalize(translateBlock(metric.field)),
        type: INDICATOR_TAG_TYPES.metric,
    }));
    const indicatorDimensionTags = indicator.dimensions.map((dimension) => ({
        value: capitalize(translateBlock(dimension.field)),
        type: INDICATOR_TAG_TYPES.dimension,
    }));
    const filterLeaves = indicator.filterTree
        ? getFilterLeaves(indicator.filterTree)
        : [];
    const seenFilterValues = new Set<string>();
    const indicatorFilterTags = filterLeaves.reduce<IndicatorTagT[]>(
        (tags, filter) => {
            const value = capitalize(translateBlock(filter.field));
            if (!seenFilterValues.has(value)) {
                seenFilterValues.add(value);
                tags.push({ value, type: INDICATOR_TAG_TYPES.filter });
            }
            return tags;
        },
        [],
    );
    const indicatorIntegrationTags = getAvailableFunctionsFromMetrics(
        indicator.metrics,
    ).map((func) => ({
        value: t(`dataCatalog.${func}`),
        type: INDICATOR_TAG_TYPES.connector,
    }));
    const indicatorTags: IndicatorTagT[] = [
        ...indicatorIntegrationTags,
        ...indicatorMetricTags,
        ...indicatorDimensionTags,
        ...indicatorFilterTags,
    ];

    if (isCityIndicator(indicator)) {
        indicatorTags.unshift({
            value: indicator.dashboardTitle,
            type: INDICATOR_TAG_TYPES.title,
        });
    }
    return indicatorTags;
}
